import type { BlogSectionToc, BlogEmbedId, KetteiEmbedId } from '../types';
import ketteiFigures from './kettei/figures.json';

export interface BlogEmbedMetadata {
  id: BlogEmbedId;
  figureNumber?: number;
  anchorId?: string;
  caption?: string;
  fallback: string;
  toc?: BlogSectionToc;
  blockSpacing?: 'body' | 'feature';
}

const ketteiEmbeds = Object.fromEntries(
  Object.entries(ketteiFigures).map(([id, figure]) => {
    const figureNumber = 'figureNumber' in figure ? figure.figureNumber : undefined;
    return [id, {
      id,
      figureNumber,
      anchorId: figureNumber ? `figure-${figureNumber}` : 'kettei-family-table',
      fallback: figure.fallback || 'Public benchmark accuracy and latency for the kettei models and comparison models.',
    }];
  }),
) as Record<KetteiEmbedId, BlogEmbedMetadata>;

export const BLOG_EMBEDS: Record<BlogEmbedId, BlogEmbedMetadata> = {
  ...ketteiEmbeds,
  'router-frontier': {
    id: 'router-frontier',
    figureNumber: 1,
    anchorId: 'figure-1',
    caption: 'RouterArena accuracy and cost as of 14 July 2026. Diamonds are fixed models, circles are routers, and the solid curve is the family lookup. The star is the submitted Sqwish policy; the dotted Sqwish sweep shows how the same fitted router moves as cost sensitivity changes. The gap to the per-request oracle is the routing value that no policy on the chart has yet recovered.',
    fallback: 'Figure 1. RouterArena accuracy and cost as of 14 July 2026. Diamonds are fixed models, circles are routers, the solid curve is the family lookup, and the dotted fixed-model envelope joins the strongest individual models across the cost range. The star marks the submitted Sqwish policy; the dotted Sqwish sweep shows how the same fitted router moves as cost sensitivity changes. The per-request oracle is the theoretical ceiling.',
  },
  'weather-map': {
    id: 'weather-map',
    figureNumber: 2,
    anchorId: 'figure-2',
    caption: 'Model-routing boundaries can be hard to learn in text-embedding space: nearby prompts may demand entirely different reasoning from an LLM. Finding positive-integer solutions to a² + b² = c² is the familiar school-level problem of Pythagorean triples. Change the exponent to n > 2 and it becomes Fermat’s Last Theorem, which resisted proof for more than 350 years. The faint paths hint at how differently the resulting rollouts can unfold. A useful routing representation should pull these nearly identical prompts apart.',
    fallback: 'Figure 2. Model-routing boundaries can be difficult to learn in text-embedding space because nearby prompts can demand very different capabilities. Changing a² + b² = c², the familiar Pythagorean relation, to aⁿ + bⁿ = cⁿ turns a school-level task into Fermat’s Last Theorem, which remained unsolved for more than 350 years.',
  },
  'routing-question-contrast': {
    id: 'routing-question-contrast',
    fallback: 'Embedding What is this text about? Routing What work will this request require? Which model in the pool is best equipped to do it?',
    blockSpacing: 'body',
  },
  'layer-routability': {
    id: 'layer-routability',
    figureNumber: 3,
    anchorId: 'figure-3',
    caption: 'We tested hidden states from different depths of MiniCPM5-1B, the small language model that encodes each request, and measured how well each readout distinguished the best model family. Routing signal peaks just after the network’s midpoint, then falls toward the output.',
    fallback: 'Figure 3. For MiniCPM5-1B, the ability of hidden-state readouts to predict the best model family peaks just after the network midpoint, then falls toward the output. The router can stop at that peak instead of serving the later layers.',
  },
  'model-fingerprints': {
    id: 'model-fingerprints',
    figureNumber: 4,
    anchorId: 'figure-4',
    caption: 'A three-dimensional projection of the capability fingerprints for models in our routing pool. Nearby points have similar combinations of benchmark performance, latency, and price. That gives the router a useful first estimate for a new model; the predictor itself uses the full fingerprint, not this display projection.',
    fallback: 'Figure 4. Model capability fingerprints projected onto their first three principal components. Models occupy different regions according to their benchmark performance, latency, and cost profiles, allowing a new model to enter the router as data rather than as a new classifier output.',
  },
  'router-architecture': {
    id: 'router-architecture',
    figureNumber: 5,
    anchorId: 'figure-5',
    caption: 'The request is encoded once. For each model-and-budget action, a small conditioner generates FiLM scales and shifts inside a TabM ensemble; its members share their main weights but produce separate accuracy and cost predictions. Cost sensitivity and risk tolerance are applied afterward, so changing either one only reranks cached predictions.',
    fallback: 'Figure 5. The query is encoded once from lightweight structural features and hidden-state readouts. For each candidate model and reasoning budget, a FiLM conditioner modulates that query representation inside a TabM-style ensemble. Every member predicts the mean and variance of accuracy and cost; cost sensitivity and risk tolerance rerank the cached outcome distributions at serving time.',
  },
  'router-playground': {
    id: 'router-playground',
    anchorId: 'router-demo',
    fallback: 'Interactive router demo. Enter a request to predict accuracy and cost for the model pool, then move cost sensitivity to rerank the same cached predictions without retraining or running the predictor again. The standalone playground can also run the selected model and show its response.',
    toc: { label: 'Try the router', level: 2, kind: 'demo' },
  },
  'synthetic-loop': {
    id: 'synthetic-loop',
    figureNumber: 6,
    anchorId: 'figure-6',
    caption: 'Starting from requests where the router makes costly mistakes or lacks evidence, our workflow uses frontier models, web research and code execution to generate new tasks. Each candidate must preserve the anchor task\'s required skills and difficulty, have a verified reference answer, pass anti-replay checks, and reproduce a useful pattern of model successes and failures. Failed candidates are repaired or discarded.',
    fallback: 'Figure 6. The targeted synthetic-data loop starts from anchors in high-regret or high-uncertainty regions. Frontier models generate and validate new tasks with web research and code execution. Candidates must preserve the anchor task\'s required skills and difficulty, have a verified reference answer, pass anti-replay checks, and reproduce a useful pattern of model successes and failures.',
  },
};

export function getBlogEmbedMetadata(id: BlogEmbedId): BlogEmbedMetadata {
  return BLOG_EMBEDS[id];
}
